import React, { ReactNode } from "react";
import { Modal, Button, Typography } from "antd";

const { Text } = Typography;

interface ColumnConfigModalProps {
  title?: string;
  open: boolean;
  onClose: () => void;
  onApply?: () => void;
  width?: number;
  children: ReactNode;
  isViewMode?: boolean;
}

interface ConfigSectionProps {
  title: string;
  children: ReactNode;
  columns?: 1 | 2 | 3 | 4;
  showDivider?: boolean;
}

interface ConfigFieldProps {
  label: string;
  children: ReactNode;
}

// Main Modal Component
export const ColumnConfigModal: React.FC<ColumnConfigModalProps> = ({
  title = "Configure Headers",
  open,
  onClose,
  onApply,
  width = 700,
  children,
  isViewMode = false,
}) => {
  const handleApply = () => {
    if (onApply) {
      onApply();
    }
    onClose();
  };

  return (
    <Modal
      title={isViewMode ? `${title} (View Only)` : title}
      open={open}
      onCancel={onClose}
      footer={
        isViewMode
          ? [
              <Button key="close" onClick={onClose}>
                Close
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onClose}>
                Cancel
              </Button>,
              <Button
                key="apply"
                type="primary"
                onClick={handleApply}
                style={{
                  backgroundColor: "#1890ff",
                  borderColor: "#1890ff",
                }}
              >
                Apply
              </Button>,
            ]
      }
      width={width}
    >
      <div className={isViewMode ? "view-mode-modal-content" : ""}>
        {children}
      </div>
    </Modal>
  );
};

// Section Component for grouping fields
export const ConfigSection: React.FC<ConfigSectionProps> = ({
  title,
  children,
  columns = 3,
  showDivider = true,
}) => {
  return (
    <>
      <div className="mb-4">
        <Text strong className="text-xs text-gray-600 uppercase">
          {title}
        </Text>
        <div
          className="mt-2"
          style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: "12px" }}
        >
          {children}
        </div>
      </div>
      {showDivider && (
        <div style={{ borderTop: "1px solid #e8e8e8", margin: "16px 0" }} />
      )}
    </>
  );
};

// Individual Field Component
export const ConfigField: React.FC<ConfigFieldProps> = ({
  label,
  children,
}) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Text
        className="text-xs mb-1 block"
        style={{
          lineHeight: "1.3",
          marginBottom: "6px",
          flexShrink: 0
        }}
      >
        {label}
      </Text>
      <div style={{ marginTop: "auto" }}>
        {children}
      </div>
    </div>
  );
};

// Select styles for consistency
export const selectStyles: React.CSSProperties = {
  width: "100%",
  padding: "6px 8px",
  borderRadius: "4px",
  border: "1px solid #d9d9d9",
  fontSize: "13px",
  height: "32px",
};

export default ColumnConfigModal;
